/**
 * storageCallbackWatch.ts — which storage work froze the page (2026-09-30).
 *
 * The health record showed Malaika's page frozen for 5 to 36 seconds at a time, every one of them
 * inside Firestore's on-device storage: the long-animation-frame entry names Firestore's handler
 * for "one storage request finished" (simple_db.ts wrapRequest). That handler runs everything
 * Firestore does next, synchronously, until its next request, so the entry cannot say WHICH work
 * it was: a read, a write of a large document, bookkeeping over many rows.
 *
 * This watch answers that from inside the page. It wraps IndexedDB's own request callback
 * (IDBRequest's onsuccess) and the object store and index calls, and when one callback runs
 * SLOW_STORAGE_CALLBACK_MS or longer it keeps: how long the callback ran, which store and which
 * kind of request had just finished, the transaction's mode, how many rows a bulk read returned,
 * and the requests the callback made while it ran, with how long each kind took (a write copies
 * its value while it is made, so a huge document shows up there). Store names come from
 * Firestore's own fixed list (chatHealthCounters' STORAGE_STORE_NAMES); anything else is 'other'.
 * The vocabulary (the lists and the shape) lives in chatHealthCounters beside the rest of the
 * record, with its server twin; this module keeps only the wrappers and what they saw.
 * Never a key, a value, a database name or anything a person wrote.
 *
 * The long-frame record asks storageCallbackDuring() for the callback its script ran, so each
 * frozen frame carries what it was doing. A frame whose storage callback was quick says so
 * (handlerMs 0): the time went to work the callback queued, not to the callback itself.
 *
 * It only measures. A wrapped callback is called exactly as before, with the same `this` and
 * event, and throws what it throws; the getter hands back the function that was set. Lever
 * REACT_APP_STORAGE_WATCH=off (firestore.ts) leaves IndexedDB untouched.
 */
import {
  MAX_STORAGE_ISSUED,
  STORAGE_OPS,
  STORAGE_STORE_NAMES,
  STORAGE_TX_MODES,
  type ChatHealthStorage,
  type ChatHealthStorageIssue,
  type ChatHealthStorageOp,
  type ChatHealthStorageStore,
  type ChatHealthStorageTxMode,
} from './chatHealthCounters';

/** A storage callback this long is kept (a frame is recorded from 2 s; this is well under it). */
export const SLOW_STORAGE_CALLBACK_MS = 250;
/** Slow callbacks kept for the frames to find (oldest dropped first). */
const MAX_RECORDS = 20;
/** How far a script's start may sit from the callback's own clock reading. */
const MATCH_SLACK_MS = 5;

const OBJECT_STORE_METHODS = [
  'get', 'getAll', 'getAllKeys', 'getKey', 'put', 'add', 'delete', 'clear', 'count', 'openCursor', 'openKeyCursor',
] as const;
const INDEX_METHODS = ['get', 'getAll', 'getAllKeys', 'getKey', 'count', 'openCursor', 'openKeyCursor'] as const;

interface Issued { store: ChatHealthStorageStore; op: ChatHealthStorageOp; n: number; ms: number }
interface Kept { start: number; end: number; cb: ChatHealthStorage }

const records: Kept[] = [];
const opOf = new WeakMap<object, ChatHealthStorageOp>();
const originals = new WeakMap<object, unknown>();
let current: Map<string, Issued> | null = null;
let installed = false;
let restore: (() => void) | null = null;

const now = (): number => {
  try {
    return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
  } catch {
    return Date.now();
  }
};

export function storeNameOf(source: unknown): ChatHealthStorageStore {
  try {
    const s = source as { name?: unknown; objectStore?: { name?: unknown } } | null;
    // An index names its object store; a store names itself.
    const name = s && typeof s.objectStore === 'object' && s.objectStore ? s.objectStore.name : s?.name;
    return typeof name === 'string' && name !== 'other' && (STORAGE_STORE_NAMES as readonly string[]).includes(name)
      ? name as ChatHealthStorageStore
      : 'other';
  } catch {
    return 'other';
  }
}

const opName = (op: string): ChatHealthStorageOp =>
  ((STORAGE_OPS as readonly string[]).includes(op) ? op as ChatHealthStorageOp : 'other');

const modeOf = (request: unknown): ChatHealthStorageTxMode => {
  try {
    const mode = (request as { transaction?: { mode?: unknown } | null }).transaction?.mode;
    return typeof mode === 'string' && (STORAGE_TX_MODES as readonly string[]).includes(mode)
      ? mode as ChatHealthStorageTxMode
      : 'other';
  } catch {
    return 'other';
  }
};

function remember(request: object, start: number, ms: number, issued: Map<string, Issued>): void {
  const op = opOf.get(request) ?? 'other';
  const list = Array.from(issued.values()).sort((a, b) => b.ms - a.ms);
  const top: ChatHealthStorageIssue[] = list.slice(0, MAX_STORAGE_ISSUED).map((i) => ({ store: i.store, op: i.op, n: i.n, ms: Math.round(i.ms) }));
  const cb: ChatHealthStorage = {
    handlerMs: Math.max(1, Math.round(ms)),
    store: storeNameOf((request as { source?: unknown }).source),
    op,
    mode: modeOf(request),
  };
  if (op === 'getAll' || op === 'getAllKeys') {
    try {
      const rows = (request as { result?: unknown }).result;
      if (Array.isArray(rows)) cb.results = rows.length;
    } catch { /* a request whose result cannot be read keeps no count */ }
  }
  if (top.length) {
    cb.issued = top;
    cb.issuedMs = Math.round(list.reduce((sum, i) => sum + i.ms, 0));
  }
  records.push({ start, end: start + ms, cb });
  if (records.length > MAX_RECORDS) records.shift();
}

function timed(request: object, fn: (this: unknown, ev: unknown) => unknown): (this: unknown, ev: unknown) => unknown {
  const wrapped = function (this: unknown, ev: unknown): unknown {
    if (current) return fn.call(this, ev);
    const issued = new Map<string, Issued>();
    current = issued;
    const start = now();
    try {
      return fn.call(this, ev);
    } finally {
      current = null;
      const ms = now() - start;
      if (ms >= SLOW_STORAGE_CALLBACK_MS) remember(request, start, ms, issued);
    }
  };
  originals.set(wrapped, fn);
  return wrapped;
}

function wrapMethods(proto: any, names: readonly string[], storeOf: (self: any) => unknown): Array<() => void> {
  const undo: Array<() => void> = [];
  if (!proto) return undo;
  for (const name of names) {
    const original = proto[name];
    if (typeof original !== 'function') continue;
    const op = opName(name);
    const replacement = function (this: unknown, ...args: unknown[]): unknown {
      const issued = current;
      const start = issued ? now() : 0;
      const result = original.apply(this, args);
      if (result && typeof result === 'object') opOf.set(result, op);
      if (issued) {
        const store = storeNameOf(storeOf(this));
        const key = `${store}.${op}`;
        const entry = issued.get(key) ?? { store, op, n: 0, ms: 0 };
        entry.n += 1;
        entry.ms += now() - start;
        issued.set(key, entry);
      }
      return result;
    };
    proto[name] = replacement;
    undo.push(() => { proto[name] = original; });
  }
  return undo;
}

/**
 * Start watching (once). False when this browser has no IndexedDB or its request callback is not
 * the standard accessor (then nothing is changed). Never throws.
 */
export function watchStorageCallbacks(): boolean {
  if (installed) return true;
  try {
    const g = globalThis as any;
    const Req = g.IDBRequest;
    if (typeof Req !== 'function' || !Req.prototype) return false;
    const desc = Object.getOwnPropertyDescriptor(Req.prototype, 'onsuccess');
    if (!desc || !desc.configurable || typeof desc.get !== 'function' || typeof desc.set !== 'function') return false;
    const undo = [
      ...wrapMethods(g.IDBObjectStore?.prototype, OBJECT_STORE_METHODS, (self) => self),
      ...wrapMethods(g.IDBIndex?.prototype, INDEX_METHODS, (self) => self),
    ];
    Object.defineProperty(Req.prototype, 'onsuccess', {
      configurable: true,
      enumerable: desc.enumerable,
      get(this: unknown) {
        const set = desc.get!.call(this);
        return (set && originals.get(set)) || set;
      },
      set(this: object, fn: unknown) {
        desc.set!.call(this, typeof fn === 'function' ? timed(this, fn as (ev: unknown) => unknown) : fn);
      },
    });
    undo.push(() => { Object.defineProperty(Req.prototype, 'onsuccess', desc); });
    restore = () => { undo.forEach((u) => u()); };
    installed = true;
    return true;
  } catch {
    return false;
  }
}

/**
 * The slow storage callback that started inside [startMs, endMs] (the page's own clock, the one a
 * long-animation-frame script's startTime uses): the longest when there are several. Null when no
 * callback that long ran then.
 */
export function storageCallbackDuring(startMs: number, endMs: number): ChatHealthStorage | null {
  let best: Kept | null = null;
  for (const r of records) {
    if (r.start < startMs - MATCH_SLACK_MS || r.start > endMs + MATCH_SLACK_MS) continue;
    if (!best || r.cb.handlerMs > best.cb.handlerMs) best = r;
  }
  return best ? best.cb : null;
}

/** Tests only: undo the wrappers and forget what was kept. */
export function resetStorageWatchForTests(): void {
  if (restore) restore();
  restore = null;
  installed = false;
  current = null;
  records.length = 0;
}
